import { DEFAULT_LANGUAGE, differenceInCalendarDays } from '@cibli/shared';
import { TFunction } from 'i18next';

/**
 * Formats a date string to localized format (DD/MM/YYYY)
 * @param dateString - ISO date string
 * @param locale - Optional locale string (defaults to DEFAULT_LANGUAGE)
 * @returns Formatted date string using specified locale
 */
export const formatDate = (dateString: string, locale?: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString(locale || DEFAULT_LANGUAGE, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

/**
 * Formats a date string to long localized format (e.g., "15 janvier 2025")
 * @param dateString - ISO date string or Date object
 * @param locale - Optional locale string (defaults to DEFAULT_LANGUAGE)
 * @returns Formatted date string with long month name
 */
export const formatLongDate = (dateString: string | Date, locale?: string): string => {
  const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
  return date.toLocaleDateString(locale || DEFAULT_LANGUAGE, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

/**
 * Returns the first day of the current month in YYYY-MM-DD format.
 * Used for "end date" / "still in progress" form fields (month input storage format).
 */
export const getCurrentMonthFirstDayISO = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

/**
 * Returns a YYYY-MM-DD string suitable for date sorting (descending = most recent first).
 * Uses startDate, then endDate, then current month when missing.
 */
export const getDateForSort = (
  startDate: string | null | undefined,
  endDate?: string | null,
): string => {
  const raw = startDate?.trim() || endDate?.trim() || getCurrentMonthFirstDayISO();
  if (!raw) return getCurrentMonthFirstDayISO();
  return raw.length === 7 ? `${raw}-01` : raw;
};

/**
 * Formats a date string to relative time (today, yesterday, X days ago)
 * @param dateString - ISO date string
 * @param t - i18next translation function
 * @returns Relative time string
 */
export const formatTimeAgo = (dateString: string, t: TFunction): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffDays = differenceInCalendarDays(now, date);

  if (diffDays === 0) return t('dashboard.jobs.today');
  if (diffDays === 1) return t('dashboard.jobs.yesterday');
  return t('dashboard.jobs.daysAgo', { days: diffDays });
};
